import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Smartphone, RefreshCw, FileDown, Eye, CheckCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { buildOperatorTasksFromProcessedRows, countTasks, sendOperatorTasks, smokeTestTaskBuilding, type OperatorTask } from '@/lib/snapshotTasks';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { apiJson, API_BASE } from '@/lib/api';
import { DistributionConfirmModal } from './DistributionConfirmModal';
import { SHOW_LEGACY_PDF_ACTIONS } from '@/lib/flags';

type OperatorAccordionProps = {
  operatorId: string;
  operatorName: string;
  storeCount: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  onSendToMobile?: () => void;
  // Optional: allow parent to control <details> element for expand/collapse all
  detailsRef?: (el: HTMLDetailsElement | null) => void;
  // New props for snapshot-driven task sending
  cycleId?: string | number;
  snapshot?: any; // Now receives processed rows directly
  initialMobileSentAt?: string | null;
  initialPdfSentAt?: string | null;
  onPreview?: () => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  briefingAvailable?: boolean;
  // Controlled "Include briefing" value. When provided, the parent owns the
  // state so it survives this component remounting after a react-query
  // refetch. When omitted, we fall back to local state.
  includeBriefing?: boolean;
  onIncludeBriefingChange?: (include: boolean) => void;
};

export function OperatorAccordion({
  operatorId,
  operatorName,
  storeCount,
  defaultOpen = false,
  children,
  onSendToMobile,
  detailsRef,
  cycleId,
  snapshot,
  initialMobileSentAt = null,
  initialPdfSentAt = null,
  onPreview,
  selected = false,
  onSelectedChange,
  briefingAvailable = false,
  includeBriefing: includeBriefingProp,
  onIncludeBriefingChange,
}: OperatorAccordionProps) {
  const detailsEl = useRef<HTMLDetailsElement | null>(null);
  const [open, setOpen] = useState<boolean>(!!defaultOpen);
  const [sending, setSending] = useState<boolean>(false);
  const [mobileSentAt, setMobileSentAt] = useState<string | null>(initialMobileSentAt);
  const [pdfSentAt, setPdfSentAt] = useState<string | null>(initialPdfSentAt);
  const [showPdfConfirm, setShowPdfConfirm] = useState(false);
  const [showMobileConfirm, setShowMobileConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [includeBriefingLocal, setIncludeBriefingLocal] = useState(false);
  const isIncludeBriefingControlled = includeBriefingProp !== undefined;
  const includeBriefing = isIncludeBriefingControlled ? !!includeBriefingProp : includeBriefingLocal;
  const setIncludeBriefing = (next: boolean) => {
    if (isIncludeBriefingControlled) {
      onIncludeBriefingChange?.(next);
    } else {
      setIncludeBriefingLocal(next);
    }
  };
  const sent = !!mobileSentAt;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (defaultOpen && detailsEl.current) {
      detailsEl.current.open = true;
      setOpen(true);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (detailsRef) detailsRef(detailsEl.current);
  }, [detailsRef]);

  // Sync props to state when they change (including when data loads)
  useEffect(() => {
    if (initialMobileSentAt !== mobileSentAt) {
      setMobileSentAt(initialMobileSentAt);
    }
  }, [initialMobileSentAt]);
  
  useEffect(() => {
    if (initialPdfSentAt !== pdfSentAt) {
      setPdfSentAt(initialPdfSentAt);
    }
  }, [initialPdfSentAt]);

  // Build tasks from processed rows (snapshot) for this operator
  const tasks = cycleId && snapshot ? buildOperatorTasksFromProcessedRows(snapshot, operatorId, operatorName, cycleId) : null;
  const taskCount = tasks ? countTasks(tasks) : 0;
  
  // Add console log once per accordion mount to prove counts
  useEffect(() => {
    if (tasks) {
      console.log('ACC_COUNTS', { 
        operatorId: String(operatorId), 
        fromProcessedSnapshot: taskCount,
        sample: tasks.items.slice(0, 3)
      });
    }
  }, [operatorId, taskCount]);

  // Run smoke test once on first mount
  useEffect(() => {
    const testPassed = smokeTestTaskBuilding();
    if (!testPassed) {
      console.warn('SMOKE_TEST_FAILED: Task building function may have issues');
    }
  }, []); // Empty dependency array = run once on mount

  // Handler for sending tasks to mobile
  const handleSendToMobile = async () => {
    if (!tasks) {
      toast({
        title: "Error",
        description: "No tasks available from snapshot",
        variant: "destructive",
      });
      return;
    }

    console.log('SEND_TASKS_PAYLOAD', { 
      operatorId: String(operatorId), 
      items: tasks.items.length,
    });

    setSending(true);
    try {
      const result = await sendOperatorTasks(tasks, {
        includeBriefing: briefingAvailable && includeBriefing,
      });
      const sentTimestamp = result.sentAt ? String(result.sentAt) : new Date().toISOString();
      setMobileSentAt(sentTimestamp);
      toast({
        title: "✅ Tasks Sent Successfully",
        description: `${taskCount} tasks sent to mobile device`,
        duration: 3000,
      });
      onSendToMobile?.();
    } catch (error) {
      console.error('Failed to send tasks:', error);
      toast({
        title: "❌ Failed to Send Tasks",
        description: (error as Error)?.message || 'Unknown error',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setSending(false);
    }
  };

  // Handler for deleting all operator data for this cycle
  const handleDelete = async () => {
    if (!cycleId) {
      toast({
        title: "Error",
        description: "Cycle ID is required",
        variant: "destructive",
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/assignments/operator-cycle`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cycleId: Number(cycleId),
          operatorId: Number(operatorId)
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle blocked by signed stores
        if (result.signedStores && Array.isArray(result.signedStores)) {
          const storeList = result.signedStores.map((s: any) => 
            `• ${s.storeName}`
          ).join('\n');
          const incompleteCount = result.incompleteStoreCount || 0;
          
          toast({
            title: "❌ Cannot Delete All",
            description: (
              <div>
                <p className="mb-2 font-medium">{result.error}</p>
                <p className="mb-2 text-sm">Completed stores:</p>
                <pre className="text-xs whitespace-pre-wrap mb-2">{storeList}</pre>
                {incompleteCount > 0 && (
                  <p className="text-sm">Use the Delete button on each incomplete store card to delete them individually.</p>
                )}
              </div>
            ),
            variant: "destructive",
            duration: 15000,
          });
          return;
        }
        throw new Error(result.error || 'Delete failed');
      }

      // Clear sent status
      setMobileSentAt(null);
      
      toast({
        title: "✅ Deleted Successfully",
        description: `Deleted ${result.deleted.mobileAssignments} assignments, ${result.deleted.postingEvents} submissions, ${result.deleted.storeVisits} store visits`,
        duration: 5000,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
      queryClient.invalidateQueries({ queryKey: ['mobile-assignments-stores', cycleId] });
      queryClient.invalidateQueries({ queryKey: ['/api/payment-reports'] });
      onSendToMobile?.(); // Refresh parent
    } catch (error: any) {
      console.error('Failed to delete:', error);
      toast({
        title: "❌ Delete Failed",
        description: error?.message || 'Unknown error',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [includeImages, setIncludeImages] = useState<boolean>(true);

  const handleGenerateOperatorPdf = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!cycleId) {
      toast({
        title: 'Error',
        description: 'Cycle ID is missing; cannot generate PDF.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setGeneratingPdf(true);
      const { ticket } = await apiJson<{ ticket: string }>('/api/pdf/ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cycleId }),
      });

      const url = `${API_BASE}/api/pdf/html/${cycleId}?ticket=${encodeURIComponent(ticket)}&operatorId=${encodeURIComponent(String(operatorId))}&operatorName=${encodeURIComponent(operatorName)}&includeImages=${includeImages}`;
      console.log('[OPERATOR_PDF] Opening with includeImages:', includeImages);
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        toast({
          title: 'Popup Blocked',
          description: 'Allow popups to view the PDF preview.',
          variant: 'destructive',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Failed to launch operator PDF preview', error);
      toast({
        title: 'PDF preview failed',
        description: error instanceof Error ? error.message : 'Unknown error launching PDF preview',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleMarkAsSent = async (method: 'pdf' | 'mobile') => {
    if (!cycleId) {
      toast({
        title: 'Error',
        description: 'Cycle ID is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await apiJson<{ success: boolean; updatedCount: number; method: string; sentAt: string }>('/api/assignments/mark-sent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method,
          cycleId: Number(cycleId),
          operatorId: Number(operatorId)
        })
      });

      // Update the appropriate sent timestamp
      if (method === 'pdf') {
        setPdfSentAt(result.sentAt || new Date().toISOString());
      } else {
        setMobileSentAt(result.sentAt || new Date().toISOString());
      }

      // Invalidate queries to refresh data
      if (cycleId) {
        queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
        // Invalidate payment reports so they regenerate with updated assignment statuses
        queryClient.invalidateQueries({ queryKey: ['/api/payment-reports'] });
        queryClient.invalidateQueries({ queryKey: ['/api/payment-reports', Number(operatorId), Number(cycleId)] });
        if (onSendToMobile) {
          onSendToMobile();
        }
      }

      toast({
        title: '✅ Assignments Marked',
        description: `Marked ${result.updatedCount} assignments as sent to operator (${method.toUpperCase()})`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Failed to mark assignments:', error);
      
      // Handle 409 "already sent" case - extract sentAt from error response
      if (error?.status === 409 && error?.body) {
        const errorData = error.body;
        
        if (errorData?.sentAt) {
          // Update state with existing sentAt timestamp
          if (method === 'pdf') {
            setPdfSentAt(errorData.sentAt);
          } else {
            setMobileSentAt(errorData.sentAt);
          }
          
          // Refresh data to ensure UI is in sync
          if (cycleId) {
            queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
          }
          
          toast({
            title: 'Already Sent',
            description: `Assignments were already marked as sent on ${new Date(errorData.sentAt).toLocaleString()}`,
            duration: 4000,
          });
          return;
        }
      }
      
      toast({
        title: '❌ Failed',
        description: (error as Error)?.message || 'Failed to mark assignments',
        variant: 'destructive',
      });
    }
  };

  return (
    <details
      ref={detailsEl}
      data-testid="operator-accordion"
      className="rounded-xl border"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary
        className="flex items-center justify-between px-3 py-2 cursor-pointer select-none"
      >
        <div className="flex items-center gap-2">
          <div
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Checkbox
              checked={selected}
              onCheckedChange={(checked) => onSelectedChange?.(checked === true)}
              aria-label={`Select ${operatorName}`}
            />
          </div>
          <ChevronRight className={open ? 'rotate-90 transition' : 'transition'} />
          <h3 className="font-medium">
            {operatorName} ({storeCount} {storeCount === 1 ? 'store' : 'stores'})
          </h3>
          {taskCount > 0 && (
            <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">
              {taskCount} tasks
            </span>
          )}
        </div>
        
        {(cycleId && snapshot) && (
          <div className="flex items-center gap-2">
            {/* ─── Legacy PDF controls (hidden via SHOW_LEGACY_PDF_ACTIONS flag in lib/flags.ts) ─── */}
            {SHOW_LEGACY_PDF_ACTIONS && (
              <>
                {/* Include Images Checkbox */}
                <div className="flex items-center space-x-1 px-2 py-1 bg-gray-50 rounded border mr-1">
                  <Checkbox 
                    id={`include-images-${operatorId}`}
                    checked={includeImages}
                    onCheckedChange={(checked) => {
                      setIncludeImages(checked === true);
                      console.log('[OPERATOR_PDF] Include images toggled:', checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label 
                    htmlFor={`include-images-${operatorId}`}
                    className="text-xs font-medium leading-none cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Include images
                  </label>
                </div>
                
                {/* Original PDF button - no status update */}
                <Button
                  onClick={handleGenerateOperatorPdf}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                  data-testid="generate-operator-pdf"
                  disabled={generatingPdf}
                >
                  {generatingPdf ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-3 h-3" />
                      Generate PDF
                    </>
                  )}
                </Button>

                {/* Mark Sent button - only marks as sent, no PDF generation */}
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPdfConfirm(true);
                  }}
                  size="sm"
                  variant="default"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="mark-sent-pdf"
                >
                  {pdfSentAt ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Marked Sent
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Mark Sent
                    </>
                  )}
                </Button>

                {pdfSentAt && (
                  <span className="text-xs text-green-700">
                    Sent on {new Date(pdfSentAt).toLocaleString()}
                  </span>
                )}

                <DistributionConfirmModal
                  open={showPdfConfirm}
                  onOpenChange={setShowPdfConfirm}
                  method="pdf"
                  operatorName={operatorName}
                  taskCount={taskCount}
                  onConfirm={async () => {
                    await handleMarkAsSent('pdf');
                  }}
                />

                {/* Preview existing mobile assignment */}
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPreview?.();
                  }}
                  disabled={!onPreview || taskCount === 0}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  title={taskCount === 0 ? 'No tasks to preview' : 'Preview assignments'}
                >
                  <Eye className="w-3 h-3" />
                  Preview
                </Button>
              </>
            )}

            {/* Send to Mobile button with confirmation */}
            <div
              className={`flex items-center space-x-1 rounded border px-2 py-1 text-xs ${
                briefingAvailable ? 'bg-white' : 'bg-gray-50 text-gray-500'
              }`}
              title={
                briefingAvailable
                  ? 'Include the saved cycle briefing in this mobile send'
                  : 'Save a cycle briefing first to enable briefing delivery'
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Checkbox
                id={`include-briefing-${operatorId}`}
                checked={includeBriefing}
                disabled={!briefingAvailable || sent || sending}
                onCheckedChange={(checked) => setIncludeBriefing(checked === true)}
              />
              <label
                htmlFor={`include-briefing-${operatorId}`}
                className="cursor-pointer font-medium leading-none"
              >
                Include briefing
              </label>
            </div>

            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowMobileConfirm(true);
              }}
              disabled={sending || taskCount === 0 || sent}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
              data-testid="send-to-mobile"
              title={
                taskCount === 0
                  ? "No tasks for this operator"
                  : sent
                    ? "Already sent - delete first to re-send"
                    : `Send ${taskCount} tasks to mobile`
              }
            >
              {sending ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Smartphone className="w-3 h-3 mr-1" />
                  {sent ? 'Sent' : 'Send to Mobile'}
                </>
              )}
            </Button>

            {/* Delete button - only show if sent */}
            {sent && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                disabled={deleting}
                size="sm"
                variant="outline"
                className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                title="Delete all assignments and submissions for this operator"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </>
                )}
              </Button>
            )}

            {sent && mobileSentAt && (
              <span className="text-xs text-green-700">
                Sent on {new Date(mobileSentAt).toLocaleString()}
              </span>
            )}

            <DistributionConfirmModal
              open={showMobileConfirm}
              onOpenChange={setShowMobileConfirm}
              method="mobile"
              operatorName={operatorName}
              taskCount={taskCount}
              includeBriefing={briefingAvailable && includeBriefing}
              briefingAvailable={briefingAvailable}
              onConfirm={async () => {
                await handleSendToMobile();
                setShowMobileConfirm(false);
              }}
            />

            {/* Delete Confirmation Modal */}
            <DistributionConfirmModal
              open={showDeleteConfirm}
              onOpenChange={setShowDeleteConfirm}
              method="delete"
              operatorName={operatorName}
              taskCount={taskCount}
              onConfirm={async () => {
                await handleDelete();
                setShowDeleteConfirm(false);
              }}
            />
          </div>
        )}
      </summary>
      <div className="px-3 pb-3 pt-1">
        {children}
      </div>
    </details>
  );
}

export default OperatorAccordion;
